import React, { useState, useEffect } from 'react';
import './GroupManager.css';

const GroupManager = ({ deviceInfo, onGroupJoined, onGameStart }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [joinedGroup, setJoinedGroup] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL;
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/groups`);
      const data = await response.json();
      
      if (data.success) {
        setGroups(data.groups.filter(group => group.status === 'waiting'));
      } else {
        setError(data.error || 'Failed to load groups');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async (groupId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/groups/${groupId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: deviceInfo.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const groupInfo = groups.find(g => g.group_id === groupId);
        const joinedGroupData = {
          groupId: groupId,
          groupName: groupInfo.name,
          playerColor: data.playerColor,
          laneNumber: data.laneNumber,
          maxPlayers: groupInfo.max_players
        };
        
        setJoinedGroup(joinedGroupData);
        setSelectedGroup(groupInfo);
        
        if (onGroupJoined) {
          onGroupJoined(joinedGroupData);
        }
      } else {
        setError(data.error || 'Failed to join group');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const leaveGroup = () => {
    setJoinedGroup(null);
    setSelectedGroup(null);
    if (onGroupJoined) {
      onGroupJoined(null);
    }
  };

  const refreshGroups = () => {
    loadGroups();
  };

  if (joinedGroup) {
    return (
      <div className="group-manager joined">
        <div className="group-info">
          <h3>🎮 Joined: {joinedGroup.groupName}</h3>
          <div className="player-details">
            <div className="player-color" style={{ backgroundColor: joinedGroup.playerColor }}>
              <span className="lane-number">{joinedGroup.laneNumber}</span>
            </div>
            <div className="player-info">
              <p><strong>Your Color:</strong> {joinedGroup.playerColor}</p>
              <p><strong>Your Lane:</strong> {joinedGroup.laneNumber}</p>
              <p><strong>Group:</strong> {joinedGroup.groupName}</p>
            </div>
          </div>
          <p className="waiting-message">
            ⏳ Waiting for admin to start the game... 
            <br />
            <small>You'll be notified when the game begins!</small>
          </p>
          <button className="btn btn-secondary" onClick={leaveGroup}>
            Leave Group
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group-manager">
      <div className="group-header">
        <h2>🎯 Join a Game Group</h2>
        <p>Choose a group to play with other players</p>
        <button className="btn btn-secondary" onClick={refreshGroups} disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh Groups'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {loading && (
        <div className="loading-message">
          ⏳ Loading available groups...
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="no-groups">
          <p>📭 No game groups are currently available.</p>
          <p>Please wait for an admin to create a new group.</p>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div className="groups-list">
          {groups.map(group => (
            <div key={group.group_id} className="group-card">
              <div className="group-header">
                <h3>{group.name}</h3>
                <span className="group-status">
                  {group.current_players || 0} / {group.max_players} Players
                </span>
              </div>
              
              <div className="group-details">
                <p><strong>Created:</strong> {new Date(group.created_at).toLocaleString()}</p>
                <p><strong>Status:</strong> 
                  <span className={`status-badge status-${group.status}`}>
                    {group.status}
                  </span>
                </p>
              </div>

              <div className="group-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => joinGroup(group.group_id)}
                  disabled={group.current_players >= group.max_players}
                >
                  {group.current_players >= group.max_players ? 'Group Full' : 'Join Group'}
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => setSelectedGroup(group)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedGroup && (
        <div className="modal-overlay" onClick={() => setSelectedGroup(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedGroup.name}</h3>
              <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="group-details">
                <p><strong>Max Players:</strong> {selectedGroup.max_players}</p>
                <p><strong>Current Players:</strong> {selectedGroup.current_players || 0}</p>
                <p><strong>Created:</strong> {new Date(selectedGroup.created_at).toLocaleString()}</p>
                <p><strong>Status:</strong> 
                  <span className={`status-badge status-${selectedGroup.status}`}>
                    {selectedGroup.status}
                  </span>
                </p>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    joinGroup(selectedGroup.group_id);
                    setSelectedGroup(null);
                  }}
                  disabled={selectedGroup.current_players >= selectedGroup.max_players}
                >
                  {selectedGroup.current_players >= selectedGroup.max_players ? 'Group Full' : 'Join Group'}
                </button>
                
                <button className="btn btn-secondary" onClick={() => setSelectedGroup(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManager;
